import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { formatCount } from './format';
import { isFiltering } from './rowFilters';
import { DensityToggle } from './DensityToggle';
import { TableControls } from './TableControls';
import { CompanyScoreHistory } from './CompanyScoreHistory';
import {
  aggregateCountLabel,
  clearFiltersHint,
  coveredSpanSuffix,
  emptyResultCauses,
  MOCK_NOTICE_ACTION,
  MOCK_NOTICE_HEADING,
  mockNoticeDetail,
  NO_MATCHING_ROWS_HEADING,
  NO_ROWS_HEADING,
  rowCountLabel,
  SCORE_ONLY_SUFFIX,
  shownOfTotalLabel,
  SUPERSEDED_DETAIL,
  SUPERSEDED_HEADING,
} from './copy';
import { FeedTableNotes } from './FeedTableNotes';
import { tableNotes } from './tableNotes';
import { aggregateSummary } from './aggregateRows';
import { FeedTableHeader } from './FeedTableHeader';
import { FeedTableRow } from './FeedTableRow';
import { PAGE_SIZE, useFeedTableView } from './useFeedTableView';

// Horizontal only: the table grows to its full height and the page scrolls it, so there's one
// scrollbar to think about instead of a box within a box.
//
// The cost is the sticky header. `position: sticky` resolves against the nearest scrolling ancestor,
// and `overflow-x: auto` makes this element one on both axes whatever overflow-y says — but with no
// height cap there is nothing to scroll vertically inside it, so a header stuck to its top never
// moves. Sticky is therefore left off the header rather than declared and inert. The frozen company
// column still works: that one sticks horizontally, which is the axis this box does scroll.
const TableScroll = styled.div`
  overflow-x: auto;
  overflow-y: visible;
`;

const Table = styled.div`
  min-width: ${({ $minWidth }) => $minWidth}px;
`;

// Hoisted rather than written as a default parameter: hiddenColumns is a dependency of the sort
// effect, and `= []` would hand it a new array on every render, re-running the effect each time.
const NO_HIDDEN_COLUMNS = [];

const StaleNotice = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
`;

// The whole table dims while it's last run's answer, so the numbers can't be mistaken for this
// run's — dimming the container rather than each row keeps it one visual statement.
const StaleWrapper = styled.div`
  ${({ $stale }) => ($stale ? 'opacity: 0.45;' : '')}
`;

// Loud on purpose: sample rows must never be mistaken for a real read.
const MockNotice = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.warning};
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
`;

export const FeedRowsTable = ({
  result,
  // The feed these rows came from. Needed only by the empty state, which has no rows to read the
  // feed's shape off — that is precisely the case it exists for.
  feedId = null,
  onDrillDown,
  canDrillDown = () => true,
  isSuperseded = false,
  hiddenColumns = NO_HIDDEN_COLUMNS,
}) => {
  const {
    columns: { core, coreColumns, optionalColumns, memberColumns, gridColumns, tableMinWidth },
    cellContext,
    hasMemberColumns,
    allRows,
    filteredRows,
    rows,
    summary,
    activeFilters,
    sort,
    aggregate,
    compact,
    visibleCount,
    historyCompanyId,
    movement,
    offerAggregate,
    actions,
  } = useFeedTableView(result, feedId, hiddenColumns);

  if (!result) return null;

  const { covered, hasUnindexed, knowableSpan, memberships, missingCount, sectors, valueScale } = summary;

  if (allRows.length === 0) {
    return (
      <Container top={8}>
        <Body5>{NO_ROWS_HEADING}</Body5>
        {emptyResultCauses(feedId).map((cause) => (
          <Container top={4} key={cause}>
            <Body7 isLowContrast>{cause}</Body7>
          </Container>
        ))}
      </Container>
    );
  }

  const visibleRows = rows.slice(0, visibleCount);
  // Counted over the filtered rows, unlike its neighbours in `summary`: this line explains an em
  // dash the reader can see right now, so it has to answer for what is on screen.
  const unscoredCount = rows.filter((r) => r.score === null).length;
  // Same rule, same reason: the paragraph explains blanks that are visible, so it is gated on there
  // being some. What it SAYS, though, still answers for the read — see previousScoreNote.
  //
  // Two facts, not one, because the column has TWO blanks and they mean opposite things. "No
  // predecessor came back" is one; "a predecessor came back and scored nothing" is the other, and on
  // a multi-input feed — whose rows are the union grid of its members' occasions — the second is the
  // NORM rather than the exception. Keying the note on the first alone put a paragraph on screen
  // denying exactly what the tooltips beneath it asserted.
  //
  // Both go silent while aggregating, and that is the correction rather than a shortcut: every
  // aggregated row has a null prevScoreKnowableAt, so the first would fire on the whole table and
  // print "this read holds no earlier occasion for that company" — which is false, and is precisely
  // the sentence previousScoreNote exists to keep off the screen. The blanks have one cause here,
  // it is the view rather than the read, and one line below says so.
  const anyWithoutPrevious = !aggregate && rows.some((r) => !r.prevScoreKnowableAt);
  const anyUnscoredPrevious = !aggregate && rows.some((r) => r.prevScoreKnowableAt && r.prevScore === null);
  // What the count line has to say instead of "N rows": aggregating changes the unit, and a number
  // whose unit silently changed is worse than no number.
  const aggregateCounts = aggregate ? aggregateSummary(rows) : null;
  return (
    <div>
      {result.isMock && (
        <MockNotice role="status">
          <Body5 bold>{MOCK_NOTICE_HEADING}</Body5>
          <Container top={4}>
            <Body7 isLowContrast>
              {mockNoticeDetail(result)} {MOCK_NOTICE_ACTION}
            </Body7>
          </Container>
        </MockNotice>
      )}

      {isSuperseded && (
        <StaleNotice role="status">
          <Body5 bold>{SUPERSEDED_HEADING}</Body5>
          <Container top={4}>
            <Body7 isLowContrast>{SUPERSEDED_DETAIL}</Body7>
          </Container>
        </StaleNotice>
      )}

      {/* `matchCount` stays in ROWS whether or not the rows are aggregated. While aggregating,
          `rows` is companies, and handing that over would read as "3 of 40 rows match" when nothing
          had been filtered out at all. The filtered row count is the honest left-hand side; the
          company number belongs to the count line below, which names its own unit. */}
      <TableControls
        filters={activeFilters}
        onFilterChange={actions.updateFilters}
        onClearFilters={actions.clearFilters}
        missingCount={missingCount}
        matchCount={aggregate ? filteredRows.length : rows.length}
        totalCount={allRows.length}
        sectors={sectors}
        indexOptions={memberships}
        hasUnindexed={hasUnindexed}
        hasInputs={hasMemberColumns}
        knowableSpan={knowableSpan}
        aggregate={aggregate}
        onAggregateChange={actions.setAggregate}
        canAggregate={offerAggregate}
      />

      {rows.length === 0 && isFiltering(activeFilters) && (
        <Container bottom={8}>
          <Body5>{NO_MATCHING_ROWS_HEADING}</Body5>
          <Container top={4}>
            <Body7 isLowContrast>{clearFiltersHint(allRows.length)}</Body7>
          </Container>
        </Container>
      )}

      {/* The covered span rides in the counter row rather than in a notice of its own. It used to
          appear only alongside a "you have been truncated" warning, which the server never actually
          sends — but "which window did these rows come from" is worth stating on every read, and
          next to the row count is where someone is already looking for it. */}
      <Container bottom={4} row spaceBetween verticallyCenter>
        <Body7 isLowContrast role="status">
          {aggregateCounts ? aggregateCountLabel(aggregateCounts) : rowCountLabel(rows.length)}
          {result.slim && SCORE_ONLY_SUFFIX}
          {covered && coveredSpanSuffix(covered)}
        </Body7>
        <Container row verticallyCenter>
          <Body7 isLowContrast>{shownOfTotalLabel(Math.min(visibleCount, rows.length), rows.length)}</Body7>
          <Container left={8}>
            <DensityToggle compact={compact} onChange={actions.setCompact} />
          </Container>
        </Container>
      </Container>

      <FeedTableNotes
        notes={tableNotes({
          result,
          core,
          aggregate,
          hasMemberColumns,
          summary,
          viewCounts: { unscoredCount, anyWithoutPrevious, anyUnscoredPrevious },
        })}
      />

      <StaleWrapper $stale={isSuperseded}>
        <TableScroll>
          <Table $minWidth={tableMinWidth} data-testid="feed-rows">
            {/* Anchored for the column-order and gutter tests: the header's cell ORDER is hardcoded
                below while its grid TRACKS come from array order, and nothing else can see them
                disagree. */}
            <FeedTableHeader
              coreColumns={coreColumns}
              optionalColumns={optionalColumns}
              memberColumns={memberColumns}
              gridColumns={gridColumns}
              sort={sort}
              onSortChange={actions.setSort}
            />

            {visibleRows.map((row) => (
              <FeedTableRow
                key={row.key}
                row={row}
                coreColumns={coreColumns}
                optionalColumns={optionalColumns}
                memberColumns={memberColumns}
                gridColumns={gridColumns}
                compact={compact}
                cellContext={cellContext}
                onDrillDown={onDrillDown}
                canDrillDown={canDrillDown}
                valueScale={valueScale}
              />
            ))}
          </Table>
        </TableScroll>
      </StaleWrapper>

      {historyCompanyId !== null && (
        <CompanyScoreHistory
          series={movement.byCompany.get(String(historyCompanyId))?.series}
          feedId={feedId}
          covered={covered}
          onDismiss={() => actions.setHistoryCompanyId(null)}
        />
      )}

      {visibleCount < rows.length && (
        <Container top={12}>
          <SkeletonButton onClick={actions.showMore}>
            Show {formatCount(Math.min(PAGE_SIZE, rows.length - visibleCount))} more
          </SkeletonButton>
        </Container>
      )}
    </div>
  );
};
